import {
  ReaderPresentationComponent,
  READER_COPY,
} from '../../shared/reader-presentation.component';
import { Component, DestroyRef, HostListener, computed, inject, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../core/auth.service';
import { I18nService } from '../../core/i18n.service';
import { DynamicFormFieldsComponent } from '../forms/dynamic-form-fields.component';
import {
  FortuneCategoryService,
  FortuneGuestGateway,
  FortuneSaleCategory,
  FortuneSaleOffer,
} from './fortune-category.service';
import { SeoService } from '../../core/seo.service';
import { SiteConfigService } from '../../core/site-config.service';
import { MarkdownHtmlPipe } from '../../shared/markdown-html.pipe';
import { AnalyticsService } from '../../core/analytics.service';
import { FormsModule } from '@angular/forms';
import {
  PaymentCardDraft,
  PaymentCardSelectorComponent,
  paymentCardValidationMessage,
} from '../../shared/payment-card-selector.component';
import { SecurePaymentOverlayComponent } from '../../shared/secure-payment-overlay.component';

const FLOW_COPY = {
  tr: {
    eyebrow: 'FAL BAKTIR',
    title: 'Falını hazırla, falcını seç, gönder.',
    prepareStep: '1. Falı hazırla',
    readerStep: '2. Falcı seç',
    sendStep: '3. Gönder',
    loading: 'Fal seçenekleri hazırlanıyor…',
    freeDaily: '24 saatte bir ücretsiz',
    backCategories: 'Fal kategorilerine dön',
    prepareTitle: 'Falını hazırla',
    prepareLead: 'Form, kredi durumundan bağımsız olarak doldurulabilir.',
    willRead: 'falını yorumlayacak',
    chooseReader: 'Falcı seç',
    continueSend: 'Falı göndermeye devam et',
    continueReader: 'Falcı seçmeye devam et',
    guide: 'FAL REHBERİ',
    about: 'hakkında',
    backForm: 'Forma dön',
    chooseFor: 'için falcı seç',
    quickSend: 'Hızlı gönder',
    quickLead: 'Falcı seçmeden gönder. Uygun olan ilk falcı falına baksın.',
    fee: 'Ücret',
    credits: 'Kredi',
    fans: 'Hayranlar',
    noOffers: 'Bu kategoriyi henüz hiçbir falcı satışa açmadı.',
    backReaders: 'Falcılara dön',
    firstReader: 'Uygun olan ilk falcı falına baksın',
    interpret: 'falını yorumlasın',
    creditCheck: 'kredi · Kredi gönderim sırasında kontrol edilir.',
    summary: 'Göndereceğin bilgiler',
    loginLead:
      'Falın hazır. Göndermek ve kredi bakiyeni kullanmak için giriş yap. Girişten sonra bu fal taslağı ve seçtiğin falcı korunur.',
    loginButton: 'Giriş yap ve falına dön',
    sending: 'Gönderiliyor…',
    sendWith: 'kredi ile falı gönder',
    categoryMissing: 'Fal kategorisi bulunamadı.',
    draftRestored: 'Kaydettiğin fal bilgileri geri yüklendi.',
    loadFailed: 'Fal kategorileri yüklenemedi.',
    required: 'Kırmızı yıldızlı zorunlu bilgileri doldurmadan devam edemezsin.',
    accepted: 'Falın alındı. Sipariş',
    insufficient:
      'Kredin yetersiz. Fal bilgilerin kaydedildi; kredi aldıktan sonra bu ekrana dönerek devam edebilirsin.',
    sendFailed: 'Fal gönderilemedi.',
    home: 'Ana Sayfa',
    fortune: 'Fal baktır',
    seoSuffix: 'Falı Baktır',
    seoDescription:
      'için formunu doldur, yorumları ve fiyatları inceleyerek falcını seç ve falını güvenle gönder.',
    duty: 'Nöbetçi',
    reading: 'Falda',
    online: 'Çevrimiçi',
    offline: 'Çevrimdışı',
    fallback: 'Fal',
  },
  en: {
    eyebrow: 'GET A READING',
    title: 'Prepare your reading, choose your reader, and send it.',
    prepareStep: '1. Prepare',
    readerStep: '2. Choose reader',
    sendStep: '3. Send',
    loading: 'Preparing reading options…',
    freeDaily: 'Free once every 24 hours',
    backCategories: 'Back to reading categories',
    prepareTitle: 'Prepare your reading',
    prepareLead: 'You can complete the form regardless of your credit balance.',
    willRead: 'will interpret your reading',
    chooseReader: 'Choose reader',
    continueSend: 'Continue to send',
    continueReader: 'Continue to choose a reader',
    guide: 'READING GUIDE',
    about: 'guide',
    backForm: 'Back to form',
    chooseFor: 'choose a reader for',
    quickSend: 'Quick send',
    quickLead: 'Send without choosing a reader. The first available reader will interpret it.',
    fee: 'Fee',
    credits: 'Credits',
    fans: 'Followers',
    noOffers: 'No reader offers this category yet.',
    backReaders: 'Back to readers',
    firstReader: 'Let the first available reader interpret it',
    interpret: 'will interpret your reading',
    creditCheck: 'credits · Your balance is checked when you send.',
    summary: 'Information you will send',
    loginLead:
      'Your reading is ready. Sign in to send it and use your credit balance. Your draft and selected reader will be kept.',
    loginButton: 'Sign in and return to your reading',
    sending: 'Sending…',
    sendWith: 'credits and send reading',
    categoryMissing: 'Reading category not found.',
    draftRestored: 'Your saved reading details were restored.',
    loadFailed: 'Reading categories could not be loaded.',
    required: 'Complete all fields marked with a red asterisk before continuing.',
    accepted: 'Your reading was received. Order',
    insufficient:
      'You do not have enough credits. Your details were saved; buy credits and return here to continue.',
    sendFailed: 'The reading could not be sent.',
    home: 'Home',
    fortune: 'Get a reading',
    seoSuffix: 'Reading',
    seoDescription:
      'Complete the form, compare reader reviews and prices, then send your reading securely.',
    duty: 'On duty',
    reading: 'In a reading',
    online: 'Online',
    offline: 'Offline',
    fallback: 'Reading',
  },
  de: {
    eyebrow: 'WAHRSAGEN',
    title: 'Bereite deine Deutung vor, wähle einen Berater und sende sie.',
    prepareStep: '1. Vorbereiten',
    readerStep: '2. Berater wählen',
    sendStep: '3. Senden',
    loading: 'Deutungen werden vorbereitet…',
    freeDaily: 'Alle 24 Stunden kostenlos',
    backCategories: 'Zurück zu den Kategorien',
    prepareTitle: 'Deutung vorbereiten',
    prepareLead: 'Du kannst das Formular unabhängig von deinem Guthaben ausfüllen.',
    willRead: 'wird deine Deutung übernehmen',
    chooseReader: 'Berater wählen',
    continueSend: 'Weiter zum Senden',
    continueReader: 'Weiter zur Beraterwahl',
    guide: 'RATGEBER',
    about: 'im Überblick',
    backForm: 'Zurück zum Formular',
    chooseFor: 'Berater wählen für',
    quickSend: 'Schnell senden',
    quickLead: 'Ohne Beraterwahl senden. Der erste verfügbare Berater übernimmt deine Deutung.',
    fee: 'Preis',
    credits: 'Credits',
    fans: 'Follower',
    noOffers: 'Noch kein Berater bietet diese Kategorie an.',
    backReaders: 'Zurück zu den Beratern',
    firstReader: 'Der erste verfügbare Berater übernimmt die Deutung',
    interpret: 'übernimmt deine Deutung',
    creditCheck: 'Credits · Das Guthaben wird beim Senden geprüft.',
    summary: 'Diese Angaben sendest du',
    loginLead:
      'Deine Deutung ist vorbereitet. Melde dich an, um sie zu senden und dein Guthaben zu verwenden. Entwurf und Beraterauswahl bleiben erhalten.',
    loginButton: 'Anmelden und zur Deutung zurückkehren',
    sending: 'Wird gesendet…',
    sendWith: 'Credits senden',
    categoryMissing: 'Kategorie nicht gefunden.',
    draftRestored: 'Deine gespeicherten Angaben wurden wiederhergestellt.',
    loadFailed: 'Kategorien konnten nicht geladen werden.',
    required: 'Fülle vor dem Fortfahren alle mit einem roten Stern markierten Pflichtfelder aus.',
    accepted: 'Deutung empfangen. Bestellung',
    insufficient:
      'Dein Guthaben reicht nicht aus. Deine Angaben wurden gespeichert; kaufe Credits und kehre anschließend zurück.',
    sendFailed: 'Die Deutung konnte nicht gesendet werden.',
    home: 'Startseite',
    fortune: 'Wahrsagen',
    seoSuffix: 'Deutung',
    seoDescription:
      'Fülle das Formular aus, vergleiche Bewertungen und Preise und sende deine Anfrage sicher.',
    duty: 'Im Dienst',
    reading: 'In Beratung',
    online: 'Online',
    offline: 'Offline',
    fallback: 'Deutung',
  },
  es: {
    eyebrow: 'CONSULTA',
    title: 'Prepara tu lectura, elige a tu vidente y envíala.',
    prepareStep: '1. Preparar',
    readerStep: '2. Elegir vidente',
    sendStep: '3. Enviar',
    loading: 'Preparando las opciones…',
    freeDaily: 'Gratis una vez cada 24 horas',
    backCategories: 'Volver a las categorías',
    prepareTitle: 'Prepara tu lectura',
    prepareLead: 'Puedes completar el formulario independientemente de tus créditos.',
    willRead: 'interpretará tu lectura',
    chooseReader: 'Elegir vidente',
    continueSend: 'Continuar para enviar',
    continueReader: 'Continuar para elegir vidente',
    guide: 'GUÍA DE LECTURA',
    about: 'información',
    backForm: 'Volver al formulario',
    chooseFor: 'elige un vidente para',
    quickSend: 'Envío rápido',
    quickLead: 'Envía sin elegir vidente. La primera persona disponible hará la lectura.',
    fee: 'Precio',
    credits: 'Créditos',
    fans: 'Seguidores',
    noOffers: 'Ningún vidente ofrece aún esta categoría.',
    backReaders: 'Volver a videntes',
    firstReader: 'La primera persona disponible hará la lectura',
    interpret: 'interpretará tu lectura',
    creditCheck: 'créditos · El saldo se comprueba al enviar.',
    summary: 'Información que enviarás',
    loginLead:
      'Tu lectura está lista. Inicia sesión para enviarla y usar tus créditos. Conservaremos el borrador y el vidente elegido.',
    loginButton: 'Iniciar sesión y volver a la lectura',
    sending: 'Enviando…',
    sendWith: 'créditos y enviar',
    categoryMissing: 'No se encontró la categoría.',
    draftRestored: 'Se restauraron los datos guardados.',
    loadFailed: 'No se pudieron cargar las categorías.',
    required: 'Completa todos los campos obligatorios marcados con un asterisco rojo.',
    accepted: 'Lectura recibida. Pedido',
    insufficient:
      'No tienes créditos suficientes. Guardamos tus datos; compra créditos y vuelve para continuar.',
    sendFailed: 'No se pudo enviar la lectura.',
    home: 'Inicio',
    fortune: 'Pedir una lectura',
    seoSuffix: 'Lectura',
    seoDescription:
      'Completa el formulario, compara opiniones y precios y envía tu consulta de forma segura.',
    duty: 'De guardia',
    reading: 'En consulta',
    online: 'En línea',
    offline: 'Desconectado',
    fallback: 'Lectura',
  },
  fr: {
    eyebrow: 'VOYANCE',
    title: 'Préparez votre voyance, choisissez votre voyant et envoyez-la.',
    prepareStep: '1. Préparer',
    readerStep: '2. Choisir un voyant',
    sendStep: '3. Envoyer',
    loading: 'Préparation des options…',
    freeDaily: 'Gratuit une fois toutes les 24 heures',
    backCategories: 'Retour aux catégories',
    prepareTitle: 'Préparez votre voyance',
    prepareLead: 'Vous pouvez remplir le formulaire quel que soit votre solde de crédits.',
    willRead: 'interprétera votre voyance',
    chooseReader: 'Choisir un voyant',
    continueSend: 'Continuer vers l’envoi',
    continueReader: 'Continuer vers le choix du voyant',
    guide: 'GUIDE DE VOYANCE',
    about: 'à propos',
    backForm: 'Retour au formulaire',
    chooseFor: 'choisissez un voyant pour',
    quickSend: 'Envoi rapide',
    quickLead: 'Envoyez sans choisir de voyant. Le premier voyant disponible s’en chargera.',
    fee: 'Tarif',
    credits: 'Crédits',
    fans: 'Abonnés',
    noOffers: 'Aucun voyant ne propose encore cette catégorie.',
    backReaders: 'Retour aux voyants',
    firstReader: 'Le premier voyant disponible s’en chargera',
    interpret: 'interprétera votre voyance',
    creditCheck: 'crédits · Le solde est vérifié lors de l’envoi.',
    summary: 'Informations envoyées',
    loginLead:
      'Votre voyance est prête. Connectez-vous pour l’envoyer et utiliser vos crédits. Le brouillon et le voyant choisi seront conservés.',
    loginButton: 'Se connecter et revenir à la voyance',
    sending: 'Envoi…',
    sendWith: 'crédits et envoyer',
    categoryMissing: 'Catégorie introuvable.',
    draftRestored: 'Vos informations enregistrées ont été restaurées.',
    loadFailed: 'Impossible de charger les catégories.',
    required: 'Remplissez tous les champs obligatoires marqués d’un astérisque rouge.',
    accepted: 'Voyance reçue. Commande',
    insufficient:
      'Votre solde est insuffisant. Vos informations ont été enregistrées ; achetez des crédits puis revenez ici.',
    sendFailed: 'Impossible d’envoyer la voyance.',
    home: 'Accueil',
    fortune: 'Demander une voyance',
    seoSuffix: 'Voyance',
    seoDescription:
      'Remplissez le formulaire, comparez les avis et les tarifs, puis envoyez votre demande en toute sécurité.',
    duty: 'De garde',
    reading: 'En consultation',
    online: 'En ligne',
    offline: 'Hors ligne',
    fallback: 'Voyance',
  },
} as const;

const CATEGORY_EXPERIENCE_COPY: Record<
  string,
  Record<string, { lead: string; facts: string[]; sampleTitle: string; sample: string }>
> = {
  tr: {
    coffee: {
      lead: 'Fincan ve tabak fotoğraflarını gönder; istersen yorumun odaklanacağı soruyu da ekle.',
      facts: [
        'Form 2–6 net fotoğraf kabul eder.',
        'Sorunu yazmak isteğe bağlıdır.',
        'Falcını ve o hizmetin kredi ücretini sonraki adımda seçersin.',
      ],
      sampleTitle: 'Temsili kahve falı örneği',
      sample:
        'Fotoğrafta fincan kenarına yakın açık bir yol görülüyor. Geleneksel yorumda bu şekil, gündemindeki seçeneğin yakında daha görünür hâle gelmesi olarak ele alınabilir.',
    },
    tarot: {
      lead: 'Sekiz tarot açılımından birini seç; kartları kendin aç veya seçimi falcıya bırak.',
      facts: [
        'Sorunu tek bir konu etrafında yazabilirsin.',
        'Açılım ve kart konumları falcıya aynı sırayla ulaşır.',
        'Falcını ve o hizmetin kredi ücretini sonraki adımda seçersin.',
      ],
      sampleTitle: 'Temsili tarot yorumu örneği',
      sample:
        'Üç Kart açılımında “şimdi” konumundaki Tılsımlar İkilisi, iki seçenek arasında denge kurma ihtiyacını sorunun bağlamında öne çıkarabilir.',
    },
    katina: {
      lead: 'İlişki bağlamını ve merak ettiğin konuyu yaz; kart seçimini Katina falcısı yapar.',
      facts: [
        'Formda müşterinin kart seçmesi gerekmez.',
        'Soru aşk, iletişim veya ilişki dinamiğine odaklanır.',
        'Falcını ve o hizmetin kredi ücretini sonraki adımda seçersin.',
      ],
      sampleTitle: 'Temsili Katina yorumu örneği',
      sample:
        '“İletişimimizde neyi daha açık ifade etmeliyim?” sorusu, kart sembolleri üzerinden karşılıklı beklentiler ve kişisel sınırlar çerçevesinde yorumlanabilir.',
    },
    palm: {
      lead: 'Avuç içinin tamamını gösteren net fotoğrafları ve yorumun odaklanacağı konuyu gönder.',
      facts: [
        'Form 1–3 el fotoğrafı kabul eder.',
        'Yorum yazılı olarak Fallarım > Sipariş detayı ekranına gelir.',
        'Falcının teslim bilgisi hizmet kartında ve son onay özetinde gösterilir. El falı, kart yerine avuç çizgileri ve el yapısını ele alır.',
      ],
      sampleTitle: 'Temsili el falı yorumu',
      sample:
        'Bu temsili örnekte kalp ve akıl çizgileri arasındaki açıklık, duygular ile düşünceler arasında kişisel alan ihtiyacı temasıyla ele alınabilir; kesin bir olay veya sağlık sonucu göstermez.',
    },
    star: {
      lead: 'Merak ettiğin konuyu, ad soyadını ve formda istenen doğum bilgilerini paylaş.',
      facts: [
        'Başlangıç formu fotoğraf veya belge istemez.',
        'Yorum yazılı olarak Fallarım > Sipariş detayı ekranına gelir.',
        'Teslim bilgisi falcı hizmet kartında ve son onay özetinde görülür. Yıldızname, kart ya da fincan yerine doğum bilgileriyle kurulan geleneksel sembolik çerçeveyi kullanır.',
      ],
      sampleTitle: 'Temsili yıldızname yorumu',
      sample:
        'Bu temsili örnekte doğum bilgileri, yeni başlangıçlarda planlı hareket etme ve kararları zamana yayma temasıyla yorumlanabilir; kader veya sonuç garantisi vermez.',
    },
    water: {
      lead: 'Yorumun odaklanacağı soruyu ve formdaki temel bilgileri yaz; başlangıçta su veya bardak fotoğrafı gerekmez.',
      facts: [
        'Sorunu tek bir konu çevresinde anlatabilirsin.',
        'Yorum yazılı olarak Fallarım > Sipariş detayı ekranına gelir.',
        'Falcının teslim bilgisi hizmet kartında ve son onay özetinde görünür. Su falı, kart veya telve yerine suyla ilişkilendirilen sembolik yorum geleneğine dayanır.',
      ],
      sampleTitle: 'Temsili su falı yorumu',
      sample:
        'Bu temsili örnekte durgun yüzeyden yayılan halkalar, tek bir konunun çevresinde büyüyen seçenekleri sakin biçimde değerlendirme temasıyla ele alınabilir; kesin gelecek bilgisi değildir.',
    },
    playingCards: {
      lead: 'Merak ettiğin konuyu ve temel bilgilerini yaz; kart destesini veya kart fotoğrafını senin yüklemen gerekmez.',
      facts: [
        'Başlangıç formunda müşteri kart seçmez.',
        'Yorum yazılı olarak Fallarım > Sipariş detayı ekranına gelir.',
        'Teslim bilgisi hizmet kartında ve son onay özetinde gösterilir. İskambil falı klasik 52 kartlık destenin sembolik dilini kullanmasıyla tarottan ayrılır.',
      ],
      sampleTitle: 'Temsili iskambil falı yorumu',
      sample:
        'Bu temsili örnekte Karo Sekizlisi ile Kupa İkilisi yan yana geldiğinde emek verilen bir konuda iletişim ve uzlaşma ihtiyacı öne çıkarılabilir; belirli bir sonucu kanıtlamaz.',
    },
    dream: {
      lead: 'Rüyada hatırladığın olayları, belirgin sembolleri ve uyandığında hissettiğin duyguyu yaz.',
      facts: [
        'Başlangıç formu fotoğraf veya çizim yüklemez.',
        'Yorum yazılı olarak Fallarım > Sipariş detayı ekranına gelir.',
        'Teslim bilgisi hizmet kartında ve son onay özetinde görünür. Rüya yorumu, fiziksel kart veya görsel yerine anlatındaki sembolleri ve bağlamı ele alır.',
      ],
      sampleTitle: 'Temsili rüya yorumu',
      sample:
        'Bu temsili örnekte kaçırılan bir tren, gündelik yaşamda yetişme baskısı veya ertelenen bir karar temasıyla ilişkilendirilebilir; psikolojik tanı ya da gelecek tahmini değildir.',
    },
  },
  en: {
    coffee: {
      lead: 'Send cup and saucer photos and optionally add the question you want the reading to focus on.',
      facts: [
        'The form accepts 2–6 clear photos.',
        'Adding a question is optional.',
        'Choose a reader and that service’s credit price in the next step.',
      ],
      sampleTitle: 'Illustrative coffee reading',
      sample:
        'A clear path appears close to the cup rim. In a traditional reading, it may be discussed as an option in your current situation becoming clearer soon.',
    },
    tarot: {
      lead: 'Choose one of eight spreads, draw the cards yourself, or leave the selection to the reader.',
      facts: [
        'Keep your question focused on one topic.',
        'The spread and card positions reach the reader in the same order.',
        'Choose a reader and that service’s credit price in the next step.',
      ],
      sampleTitle: 'Illustrative tarot reading',
      sample:
        'In a Three Card spread, the Two of Pentacles in the present position may highlight the need to balance two options in the context of the question.',
    },
    katina: {
      lead: 'Describe the relationship context and your question; the Katina reader selects the cards.',
      facts: [
        'You do not select cards in this form.',
        'The question focuses on love, communication, or relationship dynamics.',
        'Choose a reader and that service’s credit price in the next step.',
      ],
      sampleTitle: 'Illustrative Katina reading',
      sample:
        'The question “What should I express more clearly in our communication?” may be interpreted through the card symbols in terms of mutual expectations and personal boundaries.',
    },
  },
  de: {
    coffee: {
      lead: 'Sende Fotos von Tasse und Untertasse und ergänze auf Wunsch deine Frage.',
      facts: [
        'Das Formular akzeptiert 2–6 klare Fotos.',
        'Eine Frage ist optional.',
        'Berater und Preis in Credits wählst du im nächsten Schritt.',
      ],
      sampleTitle: 'Beispiel einer Kaffee-Deutung',
      sample:
        'Nahe am Tassenrand ist ein offener Weg zu sehen. Traditionell kann er als Hinweis besprochen werden, dass eine aktuelle Möglichkeit bald klarer wird.',
    },
    tarot: {
      lead: 'Wähle eine von acht Legungen, ziehe selbst Karten oder überlasse die Auswahl dem Berater.',
      facts: [
        'Formuliere deine Frage zu einem Thema.',
        'Legung und Positionen erreichen den Berater in derselben Reihenfolge.',
        'Berater und Preis in Credits wählst du im nächsten Schritt.',
      ],
      sampleTitle: 'Beispiel einer Tarot-Deutung',
      sample:
        'In einer Drei-Karten-Legung kann die Zwei der Münzen in der Gegenwartsposition das Ausbalancieren zweier Möglichkeiten betonen.',
    },
    katina: {
      lead: 'Beschreibe Beziehungskontext und Frage; der Katina-Berater wählt die Karten.',
      facts: [
        'In diesem Formular wählst du keine Karten.',
        'Die Frage richtet sich auf Liebe, Kommunikation oder Beziehungsdynamik.',
        'Berater und Preis in Credits wählst du im nächsten Schritt.',
      ],
      sampleTitle: 'Beispiel einer Katina-Deutung',
      sample:
        'Die Frage „Was sollte ich in unserer Kommunikation klarer ausdrücken?“ kann anhand der Symbole zu Erwartungen und persönlichen Grenzen gedeutet werden.',
    },
  },
  es: {
    coffee: {
      lead: 'Envía fotos de la taza y el plato y añade, si quieres, la pregunta de la lectura.',
      facts: [
        'El formulario admite 2–6 fotos claras.',
        'La pregunta es opcional.',
        'Elige al vidente y el precio en créditos del servicio en el siguiente paso.',
      ],
      sampleTitle: 'Ejemplo ilustrativo de café',
      sample:
        'Se ve un camino abierto cerca del borde. En una lectura tradicional puede comentarse como una opción de tu situación que pronto se vuelve más clara.',
    },
    tarot: {
      lead: 'Elige una de ocho tiradas, saca las cartas o deja que las elija el vidente.',
      facts: [
        'Centra la pregunta en un tema.',
        'La tirada y las posiciones llegan al vidente en el mismo orden.',
        'Elige al vidente y el precio en créditos del servicio en el siguiente paso.',
      ],
      sampleTitle: 'Ejemplo ilustrativo de tarot',
      sample:
        'En una tirada de Tres Cartas, el Dos de Oros en la posición presente puede destacar la necesidad de equilibrar dos opciones.',
    },
    katina: {
      lead: 'Describe el contexto de la relación y tu pregunta; el vidente Katina elige las cartas.',
      facts: [
        'No eliges cartas en este formulario.',
        'La pregunta se centra en amor, comunicación o dinámica de pareja.',
        'Elige al vidente y el precio en créditos del servicio en el siguiente paso.',
      ],
      sampleTitle: 'Ejemplo ilustrativo de Katina',
      sample:
        'La pregunta «¿Qué debería expresar con más claridad?» puede interpretarse mediante los símbolos en torno a expectativas y límites personales.',
    },
  },
  fr: {
    coffee: {
      lead: 'Envoyez les photos de la tasse et de la soucoupe et ajoutez votre question si vous le souhaitez.',
      facts: [
        'Le formulaire accepte 2 à 6 photos nettes.',
        'La question est facultative.',
        'Choisissez le voyant et le tarif en crédits du service à l’étape suivante.',
      ],
      sampleTitle: 'Exemple illustratif de café',
      sample:
        'Un chemin dégagé apparaît près du bord. Dans une lecture traditionnelle, il peut évoquer une option de votre situation qui devient bientôt plus claire.',
    },
    tarot: {
      lead: 'Choisissez l’un des huit tirages, tirez les cartes ou laissez le voyant les choisir.',
      facts: [
        'Concentrez la question sur un seul sujet.',
        'Le tirage et les positions arrivent au voyant dans le même ordre.',
        'Choisissez le voyant et le tarif en crédits du service à l’étape suivante.',
      ],
      sampleTitle: 'Exemple illustratif de tarot',
      sample:
        'Dans un tirage à Trois Cartes, le Deux de Deniers en position présente peut souligner le besoin d’équilibrer deux options.',
    },
    katina: {
      lead: 'Décrivez le contexte relationnel et votre question ; le voyant Katina choisit les cartes.',
      facts: [
        'Vous ne choisissez pas de cartes dans ce formulaire.',
        'La question porte sur l’amour, la communication ou la relation.',
        'Choisissez le voyant et le tarif en crédits du service à l’étape suivante.',
      ],
      sampleTitle: 'Exemple illustratif de Katina',
      sample:
        'La question « Que devrais-je exprimer plus clairement ? » peut être interprétée à travers les symboles autour des attentes et des limites personnelles.',
    },
  },
};

@Component({
  selector: 'app-fortune-category-flow',
  imports: [
    ReaderPresentationComponent,
    DecimalPipe,
    RouterLink,
    DynamicFormFieldsComponent,
    MarkdownHtmlPipe,
    FormsModule,
    PaymentCardSelectorComponent,
    SecurePaymentOverlayComponent,
  ],
  template: ` <main class="fortune-flow">
      @if (flowStage() !== 'prepare' || (!loading() && !category())) {
        <header>
          <p>{{ copy().eyebrow }}</p>
          <h1>{{ category() ? categorySeoHeading(category()!.title) : copy().title }}</h1>
          <ol>
            <li [class.active]="step() >= 1">{{ copy().prepareStep }}</li>
            <li [class.active]="step() >= 2">{{ copy().readerStep }}</li>
            <li [class.active]="step() >= 3">{{ copy().sendStep }}</li>
          </ol>
        </header>
      }
      @if (notice()) {
        <p class="notice">{{ notice() }}</p>
      }
      @if (loading()) {
        <div class="loading">{{ copy().loading }}</div>
      } @else if (!category()) {
        <section class="category-grid">
          @for (item of categories(); track item.categoryID) {
            <a class="category-card" [routerLink]="categoryLink(item)"
              ><span class="category-picture">
                @if (item.imageUrl) {
                  <img [src]="categoryImage(item.imageUrl)" [alt]="item.title" />
                } @else {
                  <b>{{ categoryLabel(item.title).slice(0, 1) }}</b>
                }</span
              ><strong>{{ categoryLabel(item.title) }}</strong>
              @if (item.isFree) {
                <small>{{ copy().freeDaily }}</small>
              }
            </a>
          }
        </section>
      } @else if (flowStage() === 'prepare') {
        <a class="back" [routerLink]="i18n.routePath('fortune')">← {{ copy().backCategories }}</a>
        <section class="prepare" id="fortune-form">
          <div>
            <h1 class="prepare-category-title">{{ categorySeoHeading(category()!.title) }}</h1>
            <h2>{{ copy().prepareTitle }}</h2>
            <small>{{ copy().prepareLead }}</small>
          </div>
          @if (categoryExperience(); as experience) {
            <section class="service-brief">
              <p>{{ experience.lead }}</p>
              <ul>
                @for (fact of experience.facts; track fact) {
                  <li>{{ fact }}</li>
                }
              </ul>
              <div class="service-truth">
                <span>{{ resultFormatLabel() }}</span
                ><span>{{ readerCopy().location }}</span>
              </div>
              @if ((category()?.verifiedReviewCount || 0) > 0) {
                <p class="verified-category-rating">
                  <b>★ {{ category()?.verifiedReviewAverage | number: '1.1-2' }}</b> ·
                  {{ category()?.verifiedReviewCount }} {{ verifiedReviewLabel() }}
                </p>
              }
              <aside>
                <small>{{ experience.sampleTitle }}</small>
                <p>{{ experience.sample }}</p>
              </aside>
            </section>
          }
          <app-dynamic-form-fields
            [fields]="fields()"
            [model]="answers"
            [errors]="errors()"
            (modelChange)="scheduleDraftTracking()"
            (uploading)="uploading.set(!!$event)"
            (uploadComplete)="scheduleDraftTracking()"
            (uploadError)="notice.set($event.message)"
          />
          @if (preselectedOffer(); as selectedOffer) {
            <div class="selected-reader">
              <span
                ><b>{{ selectedOffer.shopName || selectedOffer.sellerName }}</b>
                {{ copy().willRead }} · {{ selectedOffer.creditPrice | number: '1.0-2' }}
                {{ copy().credits }}</span
              ><button
                type="button"
                [disabled]="uploading() || advancing()"
                (click)="openSellerStep(true)"
              >
                {{ copy().chooseReader }}
              </button>
            </div>
            <button
              class="submit"
              type="button"
              [disabled]="uploading() || advancing()"
              (click)="openSubmitStep()"
            >
              {{ copy().continueSend }}
            </button>
          } @else {
            <button
              class="submit"
              type="button"
              [disabled]="uploading() || advancing()"
              (click)="openSellerStep()"
            >
              {{ copy().continueReader }}
            </button>
          }
        </section>
        @if (showCategorySeoContent()) {
          <section
            class="category-description"
            [attr.aria-labelledby]="'category-description-' + category()!.categoryID"
          >
            <small>{{ copy().guide }}</small>
            <h2 [id]="'category-description-' + category()!.categoryID">
              {{ categoryLabel(category()!.title) }} {{ copy().about }}
            </h2>
            <div
              class="category-description-content"
              [innerHTML]="category()!.description | markdownHtml"
            ></div>
            <nav class="category-closing-actions" aria-label="Sonraki adımlar">
              <a [href]="categoryPath() + '#fortune-form'">{{ prepareCtaLabel() }}</a>
              <a [routerLink]="i18n.routePath('sellers')">{{ copy().chooseReader }}</a>
              <a [routerLink]="i18n.routePath('support')">{{ i18n.t('support') }}</a>
            </nav>
          </section>
        }
      } @else if (flowStage() === 'seller') {
        <a class="back" [routerLink]="categoryPath()">← {{ copy().backForm }}</a>
        <h2>{{ copy().chooseFor }} {{ category()?.title }}</h2>
        @if (category()?.quickEnabled) {
          <button
            class="quick-send"
            type="button"
            [disabled]="uploading() || advancing()"
            (click)="openQuickSubmit()"
          >
            <b
              >{{ copy().quickSend }} · {{ category()?.quickCreditPrice | number: '1.0-2' }}
              {{ copy().credits }}</b
            ><span>{{ copy().quickLead }}</span>
          </button>
        }
        <p class="selection-support">
          {{ readerCopy().support }}
          <a [routerLink]="i18n.routePath('support')">{{ i18n.t('support') }}</a>
        </p>
        <section class="reader-grid selection-reader-grid">
          @for (item of offers(); track item.offerID) {
            <article class="reader-card">
              <button
                type="button"
                class="reader-select"
                [disabled]="advancing()"
                (click)="chooseOffer(item)"
              >
                <span class="reader-photo">
                  @if (item.profilePicture) {
                    <img [src]="item.profilePicture" [alt]="item.sellerName || item.shopName" />
                  } @else {
                    <b>{{ (item.sellerName || item.shopName).slice(0, 1) }}</b>
                  }
                </span>
                <h3>{{ item.sellerName || item.shopName }}</h3>
                @if (item.description) {
                  <p class="reader-scope">{{ item.description }}</p>
                }
                <p class="reader-rating">
                  <span aria-hidden="true">★★★★★</span> <b>%{{ ratingPercent(item.rating) }}</b>
                </p>
                <p class="reader-fact">
                  {{ copy().fee }}
                  <strong>{{ item.creditPrice | number: '1.0-2' }} {{ copy().credits }}</strong>
                </p>
                <p class="reader-fact">
                  {{ readerCopy().delivery }}
                  <strong>{{
                    item.deliveryMinutes
                      ? item.deliveryMinutes + ' ' + readerCopy().minutes
                      : readerCopy().unavailable
                  }}</strong>
                </p>
                <p class="reader-fact">
                  {{ readerCopy().questions }}
                  <strong>{{ followUpLabel(item.followUpCount, item.followUpCreditPrice) }}</strong>
                </p>
                @if ((item.followerCount || 0) > 0) {
                  <p class="reader-fact">
                    {{ copy().fans }} <strong>{{ item.followerCount }}</strong>
                  </p>
                }
                <div
                  class="reader-presence"
                  [class.online]="offerPresenceKind(item) === 'online'"
                  [class.duty]="offerPresenceKind(item) === 'duty'"
                  [class.reading]="offerPresenceKind(item) === 'reading'"
                >
                  {{ offerPresenceLabel(item) }}
                </div></button
              ><app-reader-presentation [content]="item.presentation" />
            </article>
          } @empty {
            <p>{{ copy().noOffers }}</p>
          }
        </section>
      } @else {
        <a
          class="back"
          [routerLink]="quickMode() ? categoryPath() : i18n.routePath('fortuneSellerSelect')"
          >← {{ quickMode() ? copy().backForm : copy().backReaders }}</a
        >
        <section class="prepare">
          <div>
            <p>{{ category()?.title }}</p>
            <h2>
              {{ quickMode() ? copy().firstReader : offer()?.sellerName + ' ' + copy().interpret }}
            </h2>
            <small>{{ totalCredits() | number: '1.0-2' }} {{ copy().creditCheck }}</small>
          </div>
          <section class="delivery-summary" aria-live="polite">
            <h3>{{ readerCopy().summary }}</h3>
            <dl>
              <dt>{{ readerCopy().reader }}</dt>
              <dd>
                {{
                  quote()?.sellerName ||
                    (quickMode() ? copy().firstReader : offer()?.sellerName || offer()?.shopName)
                }}
              </dd>
              <dt>{{ readerCopy().type }}</dt>
              <dd>{{ category()?.title }}</dd>
              <dt>{{ outputFormatTitle() }}</dt>
              <dd>{{ resultFormatLabel() }}</dd>
              <dt>{{ readerCopy().total }}</dt>
              <dd>
                <strong>{{ totalCredits() | number: '1.0-2' }} {{ copy().credits }}</strong>
              </dd>
              <dt>{{ readerCopy().delivery }}</dt>
              <dd>
                {{
                  quote()?.deliveryMinutes
                    ? quote()?.deliveryMinutes + ' ' + readerCopy().minutes
                    : readerCopy().unavailable
                }}
              </dd>
              <dt>{{ readerCopy().questions }}</dt>
              <dd>
                @if (quote()?.followUpCount) {
                  {{ followUpLabel(quote()?.followUpCount, quote()?.followUpCreditPrice) }} ·
                  {{ quote()?.followUpWindowHours }} {{ readerCopy().hours }}
                } @else {
                  {{ readerCopy().none }}
                }
              </dd>
            </dl>
            <p>{{ readerCopy().location }}</p>
            <p>
              {{ readerCopy().support }}
              <a [routerLink]="i18n.routePath('support')">{{ i18n.t('support') }}</a>
            </p>
          </section>
          <section class="answer-summary">
            <h3>{{ copy().summary }}</h3>
            @for (field of answeredFields(); track field.formKey || field.id) {
              <div class="summary-row">
                <b>{{ field.title || field.formKey }}</b>
                @if (answerFiles(field).length) {
                  <span class="summary-images">
                    @for (file of answerFiles(field); track file.uploadID || file.fileName) {
                      <img [src]="file.tempFile" [alt]="file.fileName || field.title" />
                    }
                  </span>
                } @else {
                  <span>{{ answerText(field) }}</span>
                }
              </div>
            }
          </section>
          @if (!auth.loggedIn()) {
            <div class="login">
              <p>{{ copy().loginLead }}</p>
              <button type="button" (click)="openLogin()">{{ copy().loginButton }}</button>
              @if (guestGateways().length) {
                <div class="guest-payment">
                  <span>veya misafir olarak devam et</span
                  ><label
                    >E-posta adresi<input
                      type="email"
                      [(ngModel)]="guestEmail"
                      autocomplete="email"
                      placeholder="ornek@email.com" /></label
                  ><small
                    >Sipariş bağlantın ve falın hazır olduğunda bilgilendirme bu adrese
                    gönderilir.</small
                  >
                  <div class="guest-methods">
                    @for (gateway of guestGateways(); track gateway.id) {
                      <button
                        type="button"
                        [disabled]="submitting()"
                        (click)="startGuestGateway(gateway)"
                      >
                        {{ submitting() ? copy().sending : guestGatewayLabel(gateway) }}
                      </button>
                    }
                  </div>
                  @if (isGuestCard()) {
                    <section class="guest-card">
                      <app-payment-card-selector
                        [cards]="[]"
                        [selectedCardID]="0"
                        [card]="guestCard"
                        [allowSave]="false"
                      />
                      <div class="guest-billing">
                        <label
                          >Ad soyad<input
                            [(ngModel)]="guestBilling.name"
                            autocomplete="name" /></label
                        ><label
                          >Telefon<input
                            [(ngModel)]="guestBilling.phone"
                            type="tel"
                            autocomplete="tel" /></label
                        ><label
                          >İl<input
                            [(ngModel)]="guestBilling.city"
                            autocomplete="address-level1" /></label
                        ><label
                          >İlçe<input
                            [(ngModel)]="guestBilling.district"
                            autocomplete="address-level2" /></label
                        ><label
                          >Posta kodu<input
                            [(ngModel)]="guestBilling.postalCode"
                            autocomplete="postal-code" /></label
                        ><label class="wide"
                          >Fatura adresi<textarea
                            [(ngModel)]="guestBilling.addressLine"
                            autocomplete="street-address"
                          ></textarea>
                        </label>
                      </div>
                    </section>
                  }
                  @if (isGuestCard() && selectedGuestGateway(); as gateway) {
                    <button
                      class="guest-pay"
                      type="button"
                      [disabled]="submitting()"
                      (click)="submitGuest(gateway)"
                    >
                      {{ submitting() ? copy().sending : guestGatewayLabel(gateway) }}
                    </button>
                  }
                </div>
              }
            </div>
          } @else {
            <button
              class="submit"
              type="button"
              [disabled]="submitting() || uploading() || !quote()"
              (click)="submit()"
            >
              {{ submitting() ? copy().sending : totalCredits() + ' ' + copy().sendWith }}
            </button>
          }
        </section>
      }
    </main>
    @if (paymentHTML()) {
      <app-secure-payment-overlay
        [html]="paymentHTML()"
        (closed)="closeGuestPayment()"
        (returned)="guestPaymentReturned()"
      />
    }`,
  styles: [
    `
      .reader-select {
        display: block;
        width: 100%;
        background: none;
        border: 0;
        font: inherit;
        color: inherit;
        text-align: left;
        padding: 0;
        cursor: pointer;
      }
      .service-brief {
        background: color-mix(in srgb, var(--accent) 7%, var(--surface));
        border: 1px solid color-mix(in srgb, var(--accent) 28%, var(--border));
        border-radius: 18px;
        display: grid;
        gap: 12px;
        padding: 18px;
      }
      .service-brief > p {
        color: var(--ink);
        font-weight: 800;
        margin: 0;
      }
      .service-brief ul {
        display: grid;
        gap: 7px;
        margin: 0;
        padding-left: 20px;
      }
      .service-truth {
        display: grid;
        gap: 5px;
      }
      .service-truth span {
        color: var(--muted);
        font-size: 14px;
      }
      .verified-category-rating {
        border-top: 1px solid var(--border);
        padding-top: 11px;
      }
      .verified-category-rating b {
        color: var(--accent);
      }
      .service-brief aside {
        background: var(--surface);
        border-left: 4px solid var(--accent);
        padding: 13px 15px;
      }
      .service-brief aside small {
        color: var(--accent);
        font-weight: 900;
      }
      .service-brief aside p {
        color: var(--ink);
        font-weight: 400;
        line-height: 1.55;
        margin: 6px 0 0;
      }
      .delivery-summary {
        border: 1px solid #aaa;
        padding: 16px;
        min-width: 0;
      }
      .delivery-summary h3 {
        margin: 0 0 12px;
      }
      .delivery-summary dl {
        display: grid;
        grid-template-columns: 1fr 2fr;
        gap: 8px;
        margin: 0;
      }
      .delivery-summary dd {
        margin: 0;
        overflow-wrap: anywhere;
      }
      .delivery-summary dt {
        font-weight: 700;
      }
      .delivery-summary p {
        font-size: 14px;
        line-height: 1.5;
        margin-top: 14px;
      }
      @media (max-width: 390px) {
        .delivery-summary dl {
          grid-template-columns: 1fr;
        }
        .delivery-summary dd {
          margin-bottom: 8px;
        }
        .fortune-flow {
          padding-left: 12px !important;
          padding-right: 12px !important;
        }
        .prepare {
          padding: 16px !important;
        }
      }
      :host {
        display: block;
      }
      .fortune-flow {
        max-width: 980px;
        margin: auto;
        padding: 42px 20px 100px;
      }
      .fortune-flow > header {
        text-align: center;
        margin-bottom: 34px;
      }
      .fortune-flow > header > p {
        color: var(--accent);
        font-weight: 900;
        letter-spacing: 0.15em;
      }
      .fortune-flow h1 {
        font-size: clamp(34px, 6vw, 70px);
        letter-spacing: -0.055em;
        line-height: 1;
        margin: 12px auto 28px;
        max-width: 900px;
      }
      .fortune-flow ol {
        display: flex;
        justify-content: center;
        gap: 8px;
        list-style: none;
        padding: 0;
      }
      .fortune-flow ol li {
        border: 1px solid var(--border);
        border-radius: 999px;
        color: var(--muted);
        padding: 9px 14px;
      }
      .fortune-flow ol li.active {
        background: var(--accent);
        color: var(--accent-contrast);
        border-color: var(--accent);
      }
      .category-grid {
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 24px 18px;
      }
      .category-card {
        align-items: center;
        color: var(--ink);
        display: flex;
        flex-direction: column;
        gap: 7px;
        text-align: center;
        text-decoration: none;
      }
      .category-picture {
        align-items: center;
        aspect-ratio: 1/1;
        background: #17201f;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        overflow: hidden;
        width: 100%;
      }
      .category-picture img {
        height: 100%;
        object-fit: cover;
        width: 100%;
      }
      .category-picture b {
        color: #fff;
        font-size: 42px;
      }
      .category-card > strong {
        font-size: 14px;
        font-weight: 400;
      }
      .reader-grid {
        display: grid;
        gap: 18px 14px;
        grid-template-columns: repeat(6, minmax(0, 1fr));
      }
      .reader-card {
        background: transparent;
        border: 0;
        color: #5a1727;
        cursor: pointer;
        font: inherit;
        min-width: 0;
        padding: 0;
        text-align: left;
      }
      .reader-photo {
        align-items: center;
        aspect-ratio: 1/1;
        background: #ddd;
        display: flex;
        justify-content: center;
        overflow: hidden;
        width: 100%;
      }
      .reader-photo img {
        height: 100%;
        object-fit: cover;
        width: 100%;
      }
      .reader-photo b {
        font-size: 32px;
      }
      .reader-card h3 {
        font:
          700 18px/1.15 Arial,
          sans-serif;
        margin: 7px 10px 5px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .reader-rating {
        align-items: center;
        color: #42546a;
        display: flex;
        gap: 7px;
        margin: 0 10px 6px;
      }
      .reader-rating span {
        color: #ff9700;
        font-size: 20px;
        letter-spacing: -2px;
      }
      .reader-fact {
        color: #ae2923;
        font-size: 15px;
        line-height: 1.25;
        margin: 4px 10px;
      }
      .reader-fact strong {
        font-size: 16px;
      }
      .reader-presence {
        border-bottom: 5px solid #999;
        color: #42546a;
        font-size: 15px;
        font-weight: 500;
        margin-top: 12px;
        padding: 8px 4px 5px;
        text-align: center;
      }
      .reader-presence.online {
        border-color: #22bd36;
      }
      .reader-presence.duty {
        border-color: #7b24bd;
      }
      .reader-presence.reading {
        border-color: #e06d18;
      }
      .quick-send {
        background: #fff8dc;
        border: 2px solid #6d34a5;
        color: #321344;
        cursor: pointer;
        display: grid;
        gap: 5px;
        margin: 0 0 20px;
        padding: 13px 16px;
        text-align: left;
        width: 100%;
      }
      .quick-send b {
        font-size: 17px;
      }
      .back {
        background: none;
        border: 0;
        color: var(--accent);
        cursor: pointer;
        display: inline-block;
        font-weight: 800;
        margin: 0 0 18px;
        text-decoration: none;
      }
      .fortune-flow .prepare-category-title {
        color: var(--accent);
        font-size: 20px;
        letter-spacing: 0;
        line-height: 1.3;
        margin: 0 0 10px;
        max-width: none;
      }
      .fortune-flow h2 {
        font-size: clamp(28px, 4vw, 44px);
        margin: 0 0 22px;
      }
      .prepare {
        background: var(--surface);
        border: 1px solid var(--border);
        border-radius: 28px;
        display: grid;
        gap: 24px;
        padding: 26px;
      }
      .prepare > div:first-child {
        border-bottom: 1px solid var(--border);
        padding-bottom: 18px;
      }
      .prepare p {
        color: var(--accent);
        font-weight: 800;
        margin: 0;
      }
      .prepare small {
        color: var(--muted);
      }
      .answer-summary {
        border: 1px solid var(--border);
        display: grid;
        gap: 10px;
        padding: 16px;
      }
      .answer-summary h3 {
        margin: 0 0 4px;
      }
      .summary-row {
        border-top: 1px solid var(--border);
        display: grid;
        gap: 5px;
        padding-top: 10px;
      }
      .summary-row > span {
        color: var(--muted);
        white-space: pre-wrap;
      }
      .summary-images {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      .summary-images img {
        border: 1px solid var(--border);
        height: 82px;
        object-fit: cover;
        width: 82px;
      }
      .category-description {
        background: #f5f5f5;
        border: 1px solid #aaa;
        color: #222;
        margin-top: 24px;
        padding: 24px;
      }
      .category-description small {
        color: var(--accent);
        display: block;
        font-size: 12px;
        font-weight: 900;
        letter-spacing: 0.08em;
        margin-bottom: 8px;
      }
      .fortune-flow .category-description > h2 {
        font-size: clamp(24px, 3vw, 34px);
        margin: 0 0 16px;
      }
      .category-description-content {
        color: #222;
      }
      :host ::ng-deep .category-description-content :is(p, ul, ol, blockquote) {
        font-size: 16px;
        line-height: 1.75;
        margin: 0 0 14px;
      }
      :host ::ng-deep .category-description-content :is(ul, ol) {
        display: grid;
        gap: 8px;
        padding-left: 24px;
      }
      :host ::ng-deep .category-description-content :is(h2, h3) {
        color: #222;
        line-height: 1.2;
        margin: 28px 0 12px;
      }
      :host ::ng-deep .category-description-content h2 {
        font-size: clamp(22px, 3vw, 30px);
      }
      :host ::ng-deep .category-description-content h3 {
        font-size: clamp(18px, 2.4vw, 24px);
      }
      :host ::ng-deep .category-description-content a {
        color: var(--accent);
        cursor: pointer;
        display: inline-flex;
        font-weight: 800;
        padding: 4px 2px;
        position: relative;
        text-decoration: underline;
        text-decoration-thickness: 2px;
        text-underline-offset: 3px;
        touch-action: manipulation;
        z-index: 2;
      }
      :host ::ng-deep .category-description-content blockquote {
        border-left: 4px solid var(--accent);
        padding-left: 14px;
      }
      .category-description-content :last-child {
        margin-bottom: 0;
      }
      .category-closing-actions {
        border-top: 1px solid #ccc;
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 24px;
        padding-top: 20px;
      }
      .category-closing-actions a {
        background: var(--accent);
        border: 1px solid var(--accent);
        border-radius: 999px;
        color: var(--accent-contrast);
        font-weight: 900;
        padding: 12px 18px;
        text-decoration: none;
      }
      .category-closing-actions a + a {
        background: transparent;
        color: var(--accent);
      }
      .selected-reader {
        align-items: center;
        background: color-mix(in srgb, var(--accent) 8%, var(--surface));
        border: 1px solid var(--border);
        display: flex;
        gap: 12px;
        justify-content: space-between;
        padding: 10px 12px;
      }
      .selected-reader button {
        white-space: nowrap;
      }
      .submit,
      .login button {
        background: var(--accent);
        border: 0;
        border-radius: 999px;
        color: var(--accent-contrast);
        cursor: pointer;
        font-weight: 900;
        padding: 15px 22px;
        text-decoration: none;
      }
      .submit:disabled {
        opacity: 0.5;
      }
      .notice,
      .loading,
      .login {
        background: color-mix(in srgb, var(--accent) 10%, var(--surface));
        border: 1px solid var(--border);
        border-radius: 18px;
        padding: 18px;
        margin: 16px 0;
      }
      .guest-payment {
        border-top: 1px solid var(--border);
        display: grid;
        gap: 10px;
        margin-top: 16px;
        padding-top: 16px;
      }
      .guest-payment label {
        display: grid;
        gap: 6px;
      }
      .guest-payment input {
        background: var(--surface);
        border: 1px solid var(--border);
        border-radius: 12px;
        font: inherit;
        padding: 12px;
      }
      .guest-payment > span {
        font-weight: 900;
      }
      .guest-payment small {
        color: var(--muted);
      }
      .guest-methods {
        display: grid;
        gap: 8px;
        grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
      }
      .guest-methods button {
        background: var(--surface);
        border: 1px solid var(--border);
        color: var(--ink);
      }
      .guest-methods button.active {
        border-color: var(--accent);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent) 18%, transparent);
      }
      .guest-card {
        border: 1px solid var(--border);
        border-radius: 18px;
        display: grid;
        gap: 16px;
        padding: 16px;
      }
      .guest-billing {
        display: grid;
        gap: 10px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .guest-billing .wide {
        grid-column: 1 / -1;
      }
      .guest-billing textarea {
        background: var(--surface);
        border: 1px solid var(--border);
        border-radius: 12px;
        color: var(--ink);
        font: inherit;
        min-height: 80px;
        padding: 12px;
        resize: vertical;
      }
      .guest-pay {
        width: 100%;
      }
      @media (max-width: 900px) {
        .category-grid {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
        .reader-grid {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
      }
      @media (max-width: 620px) {
        .guest-billing {
          grid-template-columns: 1fr;
        }
        .guest-billing .wide {
          grid-column: auto;
        }
        .fortune-flow ol {
          flex-wrap: wrap;
        }
        .category-grid,
        .reader-grid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 24px 16px;
        }
        .category-card > strong {
          font-size: 14px;
        }
        .reader-card h3 {
          font-size: 16px;
        }
        .reader-rating span {
          font-size: 17px;
        }
        .reader-rating b,
        .reader-fact {
          font-size: 13px;
        }
        .reader-fact strong {
          font-size: 14px;
        }
        .selected-reader {
          align-items: stretch;
          display: grid;
        }
        .category-description {
          padding: 18px;
        }
      }
    `,
    `
      .selection-support {
        color: var(--muted);
        font-size: 14px;
        line-height: 1.5;
        margin: -10px 0 18px;
      }
      .selection-support a,
      .delivery-summary a {
        color: var(--accent);
        font-weight: 800;
      }
      .reader-scope {
        color: var(--muted);
        display: -webkit-box;
        font-size: 13px;
        line-height: 1.4;
        margin: 0 10px 7px;
        overflow: hidden;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
      }
    `,
  ],
})
export class FortuneCategoryFlowComponent {
  protected readonly auth = inject(AuthService);
  protected readonly i18n = inject(I18nService);
  private readonly service = inject(FortuneCategoryService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly seo = inject(SeoService);
  private readonly site = inject(SiteConfigService);
  private readonly analytics = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly draftKey = 'fortune.category-order-draft.v2';
  private readonly journeyKey = 'fortune.category-order-journey.v1';
  private readonly claimKey = 'fortune.category-order-claim.v1';
  private readonly draftLifetimeMs = 2 * 60 * 60 * 1000;
  private loadEpoch = 0;
  private formStartTracked = false;
  private trackingTimer: ReturnType<typeof setTimeout> | null = null;
  private journeyQueue: Promise<void> = Promise.resolve();
  protected readonly flowStage = signal<'catalog' | 'prepare' | 'seller' | 'submit'>('catalog');
  protected readonly quote = signal<any>(null);
  private async refreshQuote() {
    const item = this.category();
    if (!item) return;
    this.quote.set(
      await this.service.quote(
        this.quickMode()
          ? { categoryID: item.categoryID, quick: true }
          : { offerID: this.offer()?.offerID },
      ),
    );
  }
  protected terms() {
    const q = this.quote();
    return {
      deliveryMinutes: q?.deliveryMinutes == null ? null : Number(q.deliveryMinutes),
      followUpCount: Number(q?.followUpCount || 0),
      followUpCreditPrice: Number(q?.followUpCreditPrice || 0),
      followUpWindowHours: Number(q?.followUpWindowHours || 48),
    };
  }
  protected readonly readerCopy = computed(() => READER_COPY[this.i18n.lang()] || READER_COPY.en);
  protected followUpLabel(countValue: unknown, priceValue: unknown): string {
    const count = Number(countValue);
    if (!Number.isFinite(count) || count <= 0) return this.readerCopy().none;
    if (
      priceValue === null ||
      priceValue === undefined ||
      String(priceValue).trim() === '' ||
      !Number.isFinite(Number(priceValue))
    )
      return `${count} · ${this.readerCopy().unknown}`;
    const price = Number(priceValue);
    return price === 0
      ? `${count} · ${this.readerCopy().included}`
      : `${count} · ${new Intl.NumberFormat(this.i18n.locale(), { maximumFractionDigits: 2 }).format(price)} ${this.readerCopy().each}`;
  }
  protected totalCredits() {
    return (
      Number(
        this.quote()?.creditPrice ??
          (this.quickMode() ? this.category()?.quickCreditPrice : this.offer()?.creditPrice),
      ) || 0
    );
  }
  protected readonly copy = computed(() => FLOW_COPY[this.i18n.lang()] || FLOW_COPY.en);
  protected categoryExperience() {
    const selected = this.category();
    if (!selected) return null;
    const kind = this.categoryKind(selected.title);
    const lang = CATEGORY_EXPERIENCE_COPY[this.i18n.lang()] ? this.i18n.lang() : 'en';
    return CATEGORY_EXPERIENCE_COPY[lang][kind] || null;
  }
  protected resultFormatLabel(): string {
    return (
      (
        {
          tr: 'Sonuç, falcının yazılı yorumu olarak Fallarım > Sipariş detayı ekranına gelir.',
          en: 'The result is delivered as the reader’s written interpretation in My readings > Order details.',
          de: 'Das Ergebnis wird als schriftliche Deutung unter Meine Deutungen > Bestelldetails bereitgestellt.',
          es: 'El resultado se entrega como interpretación escrita en Mis lecturas > Detalles del pedido.',
          fr: 'Le résultat est livré sous forme d’interprétation écrite dans Mes voyances > Détails de la commande.',
        } as Record<string, string>
      )[this.i18n.lang()] || 'The result is delivered as a written interpretation in Order details.'
    );
  }
  protected outputFormatTitle(): string {
    return (
      (
        {
          tr: 'Teslim biçimi',
          en: 'Delivery format',
          de: 'Lieferformat',
          es: 'Formato de entrega',
          fr: 'Format de livraison',
        } as Record<string, string>
      )[this.i18n.lang()] || 'Delivery format'
    );
  }
  protected verifiedReviewLabel(): string {
    return (
      (
        {
          tr: 'tamamlanmış kategori siparişinden doğrulanmış puan',
          en: 'ratings verified from completed category orders',
          de: 'Bewertungen aus abgeschlossenen Kategorie-Bestellungen',
          es: 'valoraciones verificadas de pedidos completados',
          fr: 'notes vérifiées de commandes terminées',
        } as Record<string, string>
      )[this.i18n.lang()] || 'verified ratings'
    );
  }
  protected readonly loading = signal(true);
  protected readonly categories = signal<FortuneSaleCategory[]>([]);
  protected readonly category = signal<FortuneSaleCategory | null>(null);
  protected readonly offer = signal<FortuneSaleOffer | null>(null);
  protected readonly offers = signal<FortuneSaleOffer[]>([]);
  protected readonly guestGateways = signal<FortuneGuestGateway[]>([]);
  protected readonly selectedGuestGateway = signal<FortuneGuestGateway | null>(null);
  protected readonly paymentHTML = signal('');
  protected readonly fields = signal<any[]>([]);
  protected readonly errors = signal<Record<string, string>>({});
  protected readonly notice = signal('');
  protected readonly submitting = signal(false);
  protected readonly uploading = signal(false);
  protected readonly advancing = signal(false);
  protected readonly sellerPreselected = signal(false);
  protected readonly quickMode = signal(false);
  protected readonly preselectedOffer = computed(() =>
    this.sellerPreselected() ? this.offer() : null,
  );
  protected readonly step = computed(() =>
    this.flowStage() === 'submit'
      ? 3
      : this.flowStage() === 'seller'
        ? 2
        : this.flowStage() === 'prepare'
          ? 1
          : 0,
  );
  protected answers: Record<string, unknown> = {};
  protected guestEmail = '';
  protected readonly guestCard: PaymentCardDraft = {
    cardHolderName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    reminderName: '',
    isSave: false,
  };
  protected readonly guestBilling = {
    name: '',
    email: '',
    phone: '',
    country: 'TR',
    city: '',
    district: '',
    addressLine: '',
    postalCode: '',
  };
  protected readonly showCategorySeoContent = computed(
    () =>
      this.site.config().features?.fortuneCategorySeoContent === true &&
      this.categoryDescriptionParagraphs().length > 0,
  );
  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => void this.load());
    this.destroyRef.onDestroy(() => {
      if (this.trackingTimer) clearTimeout(this.trackingTimer);
    });
    void this.load();
  }
  private async load() {
    this.quote.set(null);
    const epoch = ++this.loadEpoch;
    this.loading.set(true);
    this.notice.set('');
    this.category.set(null);
    this.offer.set(null);
    this.offers.set([]);
    this.fields.set([]);
    this.sellerPreselected.set(false);
    this.quickMode.set(false);
    this.answers = {};
    try {
      if (await this.captureGuestPaypalReturn()) return;
      const data = await this.service.catalog();
      if (epoch !== this.loadEpoch) return;
      this.categories.set(
        [...(data.categories || [])].sort((a, b) => Number(!!b.isFree) - Number(!!a.isFree)),
      );
      this.guestGateways.set(data.paymentGateways || []);
      this.notice.set('');
      const normalizePath = (value: string) =>
        (
          '/' +
          String(value || '')
            .split(/[?#]/)[0]
            .split('/')
            .filter(Boolean)
            .join('/')
        ).toLocaleLowerCase('tr-TR');
      const browserPath =
        typeof window !== 'undefined' ? window.location.pathname : this.router.url;
      const currentPath = normalizePath(browserPath);
      const routeStage = String(this.route.snapshot.data['fortuneStep'] || '');
      const requested = currentPath.endsWith(
        normalizePath(this.i18n.routePath('fortuneSellerSelect')),
      )
        ? 'seller'
        : currentPath.endsWith(normalizePath(this.i18n.routePath('fortuneSubmit')))
          ? 'submit'
          : routeStage;
      const fortuneRoot = normalizePath(this.i18n.routePath('fortune'));
      const categoryPath =
        currentPath === fortuneRoot ? '' : currentPath.slice(currentPath.lastIndexOf('/') + 1);
      const slug = decodeURIComponent(categoryPath).trim().toLocaleLowerCase('tr-TR');
      this.flowStage.set(
        requested === 'seller'
          ? 'seller'
          : requested === 'submit'
            ? 'submit'
            : slug
              ? 'prepare'
              : 'catalog',
      );
      if (this.flowStage() === 'catalog') return;
      if (this.flowStage() === 'prepare') {
        const aliases = this.site.config().features?.fortuneCategoryAliases || {};
        const canonicalSlug = String(aliases[slug] || slug)
          .trim()
          .toLocaleLowerCase('tr-TR');
        const selected = this.categories().find(
          (item) =>
            this.categorySlug(item) === canonicalSlug ||
            String(item.slug || '')
              .trim()
              .toLocaleLowerCase('tr-TR') === canonicalSlug,
        );
        if (!selected) {
          this.notice.set(this.copy().categoryMissing);
          return;
        }
        if (canonicalSlug !== slug) {
          await this.router.navigateByUrl(
            this.i18n.routePath('category', { slug: this.categorySlug(selected) }),
            { replaceUrl: true },
          );
          return;
        }
        if (!(await this.loadCategory(selected, epoch))) return;
        const draft = this.readDraft();
        if (Number(draft?.categoryID) === selected.categoryID) {
          this.answers = this.draftAnswers(draft);
          this.notice.set(this.copy().draftRestored);
        }
        void this.trackJourney('category_selected', 'prepare');
        return;
      }
      const draft = this.readDraft();
      const selected = this.categories().find(
        (item) => item.categoryID === Number(draft?.categoryID),
      );
      if (!selected) {
        await this.router.navigateByUrl(this.i18n.routePath('fortune'));
        return;
      }
      if (!(await this.loadCategory(selected, epoch))) return;
      this.answers = this.draftAnswers(draft);
      if (this.flowStage() === 'submit') {
        this.quickMode.set(!!draft?.quick);
        const selectedOffer = this.offers().find((item) => item.offerID === Number(draft?.offerID));
        if (!this.quickMode() && !selectedOffer) {
          await this.router.navigateByUrl(this.i18n.routePath('fortuneSellerSelect'));
          return;
        }
        if (selectedOffer) this.offer.set(selectedOffer);
        await this.refreshQuote();
        void this.trackJourney(
          this.quickMode() ? 'quick_submit_step_opened' : 'submit_step_opened',
          'submit',
        );
      } else void this.trackJourney('seller_step_opened', 'seller');
    } catch {
      if (epoch === this.loadEpoch) this.notice.set(this.copy().loadFailed);
    } finally {
      if (epoch === this.loadEpoch) this.loading.set(false);
    }
  }
  private async loadCategory(item: FortuneSaleCategory, epoch = this.loadEpoch): Promise<boolean> {
    this.category.set(item);
    this.offer.set(null);
    this.sellerPreselected.set(false);
    this.quickMode.set(false);
    this.answers = {};
    this.setCategorySeo(item);
    const data = await this.service.catalog({ categoryID: item.categoryID });
    if (epoch !== this.loadEpoch) return false;
    this.guestGateways.set(data.paymentGateways || this.guestGateways());
    const current =
      data.selectedCategory && Number(data.selectedCategory.categoryID) === item.categoryID
        ? { ...item, ...data.selectedCategory }
        : item;
    this.category.set(current);
    this.setCategorySeo(current);
    const preferred = Number(this.route.snapshot.queryParamMap.get('sellerID') || 0);
    const offers = [...(data.offers || [])].sort(
      (a, b) => Number(b.sellerID === preferred) - Number(a.sellerID === preferred),
    );
    this.offers.set(offers);
    this.fields.set(data.formFields || []);
    this.notice.set('');
    if (preferred > 0) {
      const selected = offers.find((candidate) => candidate.sellerID === preferred);
      if (selected) {
        this.offer.set(selected);
        this.sellerPreselected.set(true);
      }
    }
    return true;
  }
  private setCategorySeo(item: FortuneSaleCategory) {
    const name = String(item.title || this.copy().fallback).trim();
    const title = String(item.metaTitle || this.categorySeoHeading(name)).trim();
    const path = this.i18n.routePath('category', { slug: this.categorySlug(item) });
    const description = String(
      item.metaDescription || item.description || `${name} ${this.copy().seoDescription}`,
    )
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 160);
    const schema: Record<string, unknown>[] = [
      {
        '@type': 'Service',
        name: title,
        description,
        serviceType: name,
        provider: { '@type': 'Organization', name: this.site.config().name },
        areaServed: 'Worldwide',
      },
    ];
    const faqs = this.categoryFaqs(item.description || '');
    if (faqs.length)
      schema.push({
        '@type': 'FAQPage',
        mainEntity: faqs.map(([question, answer]) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
      });
    this.seo.setPage({
      title,
      description,
      path,
      canonical: path,
      image: item.imageUrl,
      type: 'website',
      breadcrumbs: [
        { name: this.copy().home, path: '/' },
        { name: this.copy().fortune, path: this.i18n.routePath('fortune') },
        { name, path },
      ],
      schema,
    });
  }
  protected categoryPath() {
    const selected = this.category();
    return selected
      ? this.i18n.routePath('category', { slug: this.categorySlug(selected) })
      : this.i18n.routePath('fortune');
  }
  protected async openSellerStep(changeSeller = false) {
    if (this.advancing() || !this.validateForm()) return;
    if (changeSeller) {
      this.offer.set(null);
      this.sellerPreselected.set(false);
    }
    this.saveDraft(false);
    await this.advanceTo(
      this.i18n.routePath('fortuneSellerSelect'),
      changeSeller ? 'seller_change_requested' : 'form_completed',
      'seller',
    );
  }
  protected async openSubmitStep() {
    if (this.advancing() || !this.validateForm()) return;
    const selected = this.offer();
    if (!this.sellerPreselected() || !selected) {
      await this.openSellerStep();
      return;
    }
    this.saveDraft();
    await this.advanceTo(this.i18n.routePath('fortuneSubmit'), 'form_completed', 'submit');
  }
  protected async openQuickSubmit() {
    const category = this.category();
    if (this.advancing() || !category?.quickEnabled || !this.validateForm()) return;
    this.quickMode.set(true);
    this.offer.set(null);
    this.saveDraft(false);
    await this.advanceTo(this.i18n.routePath('fortuneSubmit'), 'quick_send_selected', 'submit');
  }
  protected async chooseOffer(item: FortuneSaleOffer) {
    if (this.advancing()) return;
    this.quickMode.set(false);
    this.offer.set(item);
    this.notice.set('');
    this.saveDraft();
    await this.advanceTo(this.i18n.routePath('fortuneSubmit'), 'seller_selected', 'submit');
  }
  private async advanceTo(
    path: string,
    eventType: string,
    stage: 'seller' | 'submit',
  ): Promise<void> {
    if (this.advancing()) return;
    this.advancing.set(true);
    if (this.trackingTimer) {
      clearTimeout(this.trackingTimer);
      this.trackingTimer = null;
    }
    void this.trackJourney(eventType, stage);
    try {
      await this.router.navigateByUrl(path);
    } finally {
      this.advancing.set(false);
    }
  }
  protected saveDraft(requireOffer = true) {
    const category = this.category(),
      offer = this.offer();
    if (!category || (requireOffer && !offer && !this.quickMode()) || typeof window === 'undefined')
      return;
    const serialized = JSON.stringify({
      categoryID: category.categoryID,
      categorySlug: category.slug,
      offerID: offer?.offerID || 0,
      quick: this.quickMode(),
      answers: this.answers,
      journeyToken: this.journeyToken(),
      claimToken: this.claimToken(),
      savedAt: Date.now(),
    });
    sessionStorage.setItem(this.draftKey, serialized);
    localStorage.setItem(this.draftKey, serialized);
  }
  protected openLogin(): void {
    this.saveDraft();
    void this.trackJourney('auth_started', 'auth');
    this.auth.requestLogin('login', 'paid_fortune');
  }
  protected startGuestGateway(gateway: FortuneGuestGateway): void {
    this.selectedGuestGateway.set(gateway);
    this.notice.set('');
    if (String(gateway.method || '').toLowerCase() !== 'creditcard' || gateway.hosted) {
      void this.submitGuest(gateway);
    }
  }
  protected isGuestCard(): boolean {
    const gateway = this.selectedGuestGateway();
    return String(gateway?.method || '').toLowerCase() === 'creditcard' && !gateway?.hosted;
  }
  protected guestGatewayLabel(gateway: FortuneGuestGateway): string {
    const method = String(gateway.method || '').toLowerCase();
    const payment = this.guestPaymentQuote(gateway);
    const amount = payment
      ? new Intl.NumberFormat(this.i18n.locale(), {
          style: 'currency',
          currency: payment.currency,
        }).format(payment.amount)
      : '';
    if (method === 'paypal') return 'PayPal ile ' + (amount || 'ödemeni') + ' öde';
    if (method === 'creditcard') return 'Kredi kartı ile ' + (amount || 'ödemeni') + ' öde';
    return String(gateway.title || gateway.method) + ' ile devam et';
  }
  private guestPaymentQuote(
    gateway: FortuneGuestGateway,
  ): { gatewayID: number; amount: number; currency: string } | null {
    const rows = Array.isArray(this.quote()?.guestPaymentQuotes)
      ? this.quote().guestPaymentQuotes
      : [];
    const row = rows.find((item: any) => Number(item?.gatewayID || 0) === gateway.id);
    if (!row || !Number.isFinite(Number(row.amount)) || !String(row.currency || '')) return null;
    return {
      gatewayID: Number(row.gatewayID),
      amount: Number(row.amount),
      currency: String(row.currency),
    };
  }
  protected async submitGuest(gateway: FortuneGuestGateway): Promise<void> {
    if (this.submitting() || this.uploading()) return;
    const email = this.guestEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.notice.set('Geçerli bir e-posta adresi yaz.');
      return;
    }
    const isCard = String(gateway.method || '').toLowerCase() === 'creditcard';
    if (isCard) {
      const cardError = paymentCardValidationMessage(this.guestCard, this.i18n.lang());
      if (cardError) {
        this.notice.set(cardError);
        return;
      }
      const billing = this.guestBilling;
      if (
        !billing.name.trim() ||
        !billing.phone.trim() ||
        !billing.city.trim() ||
        !billing.postalCode.trim() ||
        !billing.addressLine.trim()
      ) {
        this.notice.set('Ad soyad, telefon, il, posta kodu ve fatura adresini doldur.');
        return;
      }
    }
    const selected = this.offer(),
      category = this.category();
    if (!category || !this.quote() || (!selected && !this.quickMode())) return;
    this.submitting.set(true);
    this.notice.set('');
    this.saveDraft();
    try {
      const token = this.journeyToken();
      const claim = this.claimToken();
      const payment: Record<string, unknown> = { paymentGatewayID: gateway.id, email };
      if (isCard) {
        payment['creditCard'] = { ...this.guestCard, isSave: false };
        payment['billing'] = { ...this.guestBilling, email };
      }
      const order = this.quickMode()
        ? await this.service.quickOrder(
            category.categoryID,
            this.answers,
            token,
            this.totalCredits(),
            this.terms(),
            Number(this.quote()?.sellerID || 0),
            claim,
            payment,
          )
        : await this.service.order(
            selected!.offerID,
            this.answers,
            token,
            this.totalCredits(),
            this.terms(),
            undefined,
            claim,
            payment,
          );
      const redirect = String(order['paymentRedirect'] || '');
      const html = String(order['paymentHTML'] || '');
      if (redirect) {
        window.location.assign(redirect);
        return;
      }
      if (html) {
        this.paymentHTML.set(html);
        return;
      }
      throw new Error('payment_form_missing');
    } catch (error: any) {
      const body = error?.error || {};
      this.notice.set(String(body?.message || error?.message || this.copy().sendFailed));
    } finally {
      this.submitting.set(false);
    }
  }
  protected closeGuestPayment(): void {
    this.paymentHTML.set('');
  }
  protected guestPaymentReturned(): void {
    this.paymentHTML.set('');
    this.notice.set('Ödeme sonucu doğrulanıyor. Sipariş bağlantın e-posta adresine gönderilecek.');
  }
  private async captureGuestPaypalReturn(): Promise<boolean> {
    if (typeof window === 'undefined') return false;
    const query = this.route.snapshot.queryParamMap;
    if (query.get('payment') !== 'paypal-return') return false;
    const checkoutID = Number(query.get('checkoutID') || 0);
    const paypalOrderID = String(query.get('token') || '');
    if (checkoutID <= 0 || !paypalOrderID) return false;
    const result = await this.service.capturePaypal(checkoutID, paypalOrderID);
    const orderLink = String(result['orderLink'] || '');
    this.clearDraft();
    if (orderLink) {
      window.location.assign(orderLink);
      return true;
    }
    const orderID = Number(result['orderID'] || 0);
    if (orderID > 0) {
      await this.router.navigateByUrl(
        this.i18n.routePath('orderDetail', { slug: String(orderID) }),
      );
      return true;
    }
    throw new Error('paypal_capture_failed');
  }
  @HostListener('window:aymudo-auth-completed', ['$event'])
  protected onAuthCompleted(event: Event): void {
    const detail = (event as CustomEvent).detail || {};
    if (!this.category()) return;
    const authMode = detail.mode === 'register' ? 'new_registration' : 'existing_login';
    void this.trackJourney('account_linked', 'auth', authMode).then(() =>
      this.trackJourney('draft_restored', 'restored', authMode),
    );
  }
  protected answeredFields(): any[] {
    return this.fields().filter((field) => {
      const value = this.answers[String(field?.formKey || `field_${field?.id || 'x'}`)];
      return Array.isArray(value)
        ? value.length > 0
        : value !== null && value !== undefined && String(value).trim() !== '';
    });
  }
  protected answerFiles(field: any): any[] {
    const value = this.answers[String(field?.formKey || `field_${field?.id || 'x'}`)];
    return Array.isArray(value) &&
      value.some(
        (item) => item && typeof item === 'object' && ('uploadID' in item || 'tempFile' in item),
      )
      ? value
      : [];
  }
  protected answerText(field: any): string {
    const value = this.answers[String(field?.formKey || `field_${field?.id || 'x'}`)];
    const options = Array.isArray(field?.options) ? field.options : [];
    const option = options.find(
      (item: any) => String(item?.value ?? item?.id ?? item?.title ?? '') === String(value ?? ''),
    );
    if (option) return String(option.label ?? option.title ?? option.value);
    if (Array.isArray(value)) return value.map((item) => String(item)).join(', ');
    if (value && typeof value === 'object')
      return Object.entries(value)
        .filter(([, selected]) => !!selected)
        .map(([key]) => key)
        .join(', ');
    return String(value ?? '—');
  }
  private readDraft(): any {
    if (typeof window === 'undefined') return null;
    for (const storage of [sessionStorage, localStorage]) {
      try {
        const draft = JSON.parse(storage.getItem(this.draftKey) || 'null');
        if (!draft || typeof draft !== 'object') continue;
        const savedAt = Number(draft.savedAt || 0);
        if (storage === localStorage && (!savedAt || Date.now() - savedAt > this.draftLifetimeMs)) {
          storage.removeItem(this.draftKey);
          continue;
        }
        if (storage === localStorage) sessionStorage.setItem(this.draftKey, JSON.stringify(draft));
        const token = String(draft.journeyToken || '');
        if (token && !sessionStorage.getItem(this.journeyKey))
          sessionStorage.setItem(this.journeyKey, token);
        const claim = String(draft.claimToken || '');
        if (claim && !sessionStorage.getItem(this.claimKey))
          sessionStorage.setItem(this.claimKey, claim);
        return draft;
      } catch {
        storage.removeItem(this.draftKey);
      }
    }
    return null;
  }
  private clearDraft(): void {
    if (typeof window === 'undefined') return;
    sessionStorage.removeItem(this.draftKey);
    localStorage.removeItem(this.draftKey);
    sessionStorage.removeItem(this.journeyKey);
    sessionStorage.removeItem(this.claimKey);
  }
  private draftAnswers(draft: any): Record<string, unknown> {
    return draft?.answers && typeof draft.answers === 'object' ? draft.answers : {};
  }
  private journeyToken(): string {
    if (typeof window === 'undefined') return '';
    let token = sessionStorage.getItem(this.journeyKey) || '';
    if (!token) {
      token =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID().replace(/-/g, '')
          : `${Date.now()}_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(this.journeyKey, token);
    }
    return token;
  }
  private claimToken(): string {
    if (typeof window === 'undefined') return '';
    let token = sessionStorage.getItem(this.claimKey) || '';
    if (!token) {
      token =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? `${crypto.randomUUID().replace(/-/g, '')}${crypto.randomUUID().replace(/-/g, '')}`
          : `${Date.now()}_${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(this.claimKey, token);
    }
    return token;
  }
  private trackJourney(
    eventType: string,
    stage: string = this.flowStage(),
    authMode?: 'existing_login' | 'new_registration',
  ): Promise<void> {
    const category = this.category(),
      offer = this.offer(),
      journeyToken = this.journeyToken(),
      claimToken = this.claimToken(),
      answers = { ...this.answers };
    if (!category || !journeyToken) return Promise.resolve();
    const payload: Record<string, unknown> = {
      journeyToken,
      claimToken,
      eventType,
      stage,
      categoryID: category.categoryID,
      offerID: offer?.offerID || 0,
      answers,
    };
    if (authMode) payload['authMode'] = authMode;
    this.journeyQueue = this.journeyQueue
      .then(() => this.service.trackJourney(payload))
      .catch(() => {
        /* İzleme hatası müşterinin fal akışını durdurmaz. */
      });
    return this.journeyQueue;
  }
  protected scheduleDraftTracking(): void {
    this.saveDraft(false);
    if (!this.formStartTracked && this.category()) {
      this.formStartTracked = true;
      this.analytics.track('fortune.form_start', {
        entityType: 'category',
        entityID: this.category()!.categoryID,
        metadata: { mode: 'paid' },
      });
    }
    if (this.trackingTimer) clearTimeout(this.trackingTimer);
    this.trackingTimer = setTimeout(() => {
      this.trackingTimer = null;
      void this.trackJourney('form_updated', 'prepare');
    }, 800);
  }
  private validateForm(): boolean {
    const next: Record<string, string> = {};
    for (const field of this.fields()) {
      const key = String(field?.formKey || `field_${field?.id || 'x'}`);
      const value = this.answers[key];
      const required = field?.required === true || Number(field?.required || 0) === 1;
      const type = String(field?.type || '').toLowerCase();
      const isFile = ['image', 'picture', 'file', 'media', 'gallery', 'video'].includes(type);
      const minFiles = Math.max(
        0,
        Number(field?.minFiles ?? field?.minLength ?? field?.minlenght ?? field?.minlength ?? 0) ||
          0,
      );
      const empty = isFile
        ? !Array.isArray(value) ||
          value.filter((item: any) => Number(item?.uploadID || 0) > 0).length <
            Math.max(required ? 1 : 0, minFiles)
        : Array.isArray(value)
          ? value.length === 0
          : value && typeof value === 'object'
            ? Object.values(value).every((item) => !item)
            : String(value ?? '').trim() === '';
      if (required && empty) {
        next[key] = 'required';
        continue;
      }
      if (!isFile) {
        const length = String(value ?? '').trim().length;
        const min = Math.max(
          0,
          Number(field?.minLength ?? field?.minlenght ?? field?.minlength ?? 0) || 0,
        );
        const max = Math.max(
          0,
          Number(field?.maxLength ?? field?.maxlenght ?? field?.maxlength ?? 0) || 0,
        );
        if (!empty && min > 0 && length < min) next[key] = 'minlength';
        else if (!empty && max > 0 && length > max) next[key] = 'maxlength';
      }
    }
    this.errors.set(next);
    if (Object.keys(next).length) {
      this.notice.set(this.copy().required);
      if (typeof window !== 'undefined')
        window.scrollTo({
          top:
            Math.max(0, document.querySelector('.prepare')?.getBoundingClientRect().top || 0) +
            window.scrollY -
            120,
          behavior: 'smooth',
        });
      return false;
    }
    this.notice.set('');
    return true;
  }
  protected async submit() {
    if (this.submitting() || this.uploading()) return;
    const selected = this.offer(),
      category = this.category();
    if (!category || !this.quote() || (!selected && !this.quickMode())) return;
    this.submitting.set(true);
    this.notice.set('');
    void this.trackJourney('submit_attempted', 'submit');
    try {
      const token = this.journeyToken(),
        claim = this.claimToken();
      const order = this.quickMode()
        ? await this.service.quickOrder(
            category.categoryID,
            this.answers,
            token,
            this.totalCredits(),
            this.terms(),
            Number(this.quote()?.sellerID || 0),
            claim,
          )
        : await this.service.order(
            selected!.offerID,
            this.answers,
            token,
            this.totalCredits(),
            this.terms(),
            undefined,
            claim,
          );
      this.analytics.track('fortune.paid_completion', {
        entityType: 'category',
        entityID: category.categoryID,
        metadata: {
          offerID: selected?.offerID || 0,
          orderID: Number(order['orderID'] || 0),
          quick: this.quickMode(),
        },
      });
      this.clearDraft();
      this.notice.set(`${this.copy().accepted} #${order['orderID']}`);
      await this.router.navigateByUrl(
        this.i18n.routePath('orderDetail', { slug: String(order['orderID'] || '') }),
      );
    } catch (error: any) {
      const body = error?.error || {};
      if (['fortune_price_changed', 'fortune_terms_changed'].includes(body?.code)) {
        const latest = await this.service.catalog({ categoryID: category.categoryID });
        this.offers.set(latest.offers || []);
        const updated = (latest.offers || []).find((item) => item.offerID === selected?.offerID);
        if (updated) this.offer.set(updated);
        const c = (latest.categories || []).find((item) => item.categoryID === category.categoryID);
        if (c) this.category.set(c);
        await this.refreshQuote();
        this.notice.set(this.readerCopy().changed);
        return;
      }
      if (body?.code === 'insufficient_credit') {
        this.saveDraft();
        void this.trackJourney('insufficient_credit', 'submit');
        this.notice.set(this.copy().insufficient);
        const query = new URLSearchParams({ fortuneReturn: this.i18n.routePath('fortuneSubmit') });
        await this.router.navigateByUrl(
          `${this.i18n.routePath('creditTopUp')}?${query.toString()}`,
        );
      } else {
        void this.trackJourney('submit_failed', 'submit');
        this.errors.set(body?.errors || {});
        if (body?.code === 'fortune_form_invalid')
          await this.router.navigateByUrl(this.categoryPath());
        this.notice.set(this.copy().sendFailed);
      }
    } finally {
      this.submitting.set(false);
    }
  }
  protected categoryLabel(title: string) {
    return String(title || this.copy().fallback)
      .replace(/^Online\s+/i, '')
      .split(/\s+[–—-]\s+/)[0]
      .replace(/\s+Baktır$/i, '')
      .trim();
  }
  private categoryKind(
    title: string,
  ):
    'coffee' | 'tarot' | 'katina' | 'palm' | 'star' | 'water' | 'playingCards' | 'dream' | 'other' {
    const value = String(title || '').toLocaleLowerCase(this.i18n.locale());
    if (/kahve|coffee|kaffee|café|cafe/.test(value)) return 'coffee';
    if (/tarot/.test(value)) return 'tarot';
    if (/katina/.test(value)) return 'katina';
    if (/el fal|palm|hand reading|chirom/.test(value)) return 'palm';
    if (/yıldızname|yildizname/.test(value)) return 'star';
    if (/su fal|water reading|hydromancy/.test(value)) return 'water';
    if (/iskambil|playing card|cartomancy/.test(value)) return 'playingCards';
    if (/rüya|dream|traum|sueño|rêve/.test(value)) return 'dream';
    return 'other';
  }
  protected categorySeoHeading(title: string) {
    const label = this.categoryLabel(title),
      lang = this.i18n.lang(),
      kind = this.categoryKind(label);
    const fixed: Record<string, Record<string, string>> = {
      coffee: {
        en: 'Online Coffee Cup Reading',
        de: 'Kaffeesatzlesen online',
        es: 'Lectura de posos de café en línea',
        fr: 'Lecture du marc de café en ligne',
      },
      tarot: {
        en: 'Online Tarot Reading',
        de: 'Tarotkarten online legen lassen',
        es: 'Lectura de tarot en línea',
        fr: 'Tirage de tarot en ligne',
      },
      katina: {
        en: 'Katina Card Reading',
        de: 'Katina-Kartenlegung',
        es: 'Lectura de cartas Katina',
        fr: 'Lecture des cartes Katina',
      },
      dream: {
        en: 'Online Dream Interpretation',
        de: 'Traumdeutung online',
        es: 'Interpretación de sueños en línea',
        fr: 'Interprétation des rêves en ligne',
      },
    };
    if (lang !== 'tr') return fixed[kind]?.[lang] || `${label} ${this.copy().seoSuffix}`;
    if (/\bfalı$/i.test(label)) return `${label} Baktır`;
    if (/^rüya yorumu$/i.test(label)) return 'Online Rüya Yorumu';
    if (/^numeroloji$/i.test(label)) return 'Numeroloji Yorumu';
    if (/^yıldızname$/i.test(label)) return 'Yıldızname Baktır';
    if (/\bfallar$/i.test(label)) return `${label.replace(/fallar$/i, 'Fal')} Baktır`;
    return `${label} ${this.copy().seoSuffix}`;
  }
  protected prepareCtaLabel(): string {
    const label = this.categoryLabel(this.category()?.title || ''),
      lang = this.i18n.lang(),
      kind = this.categoryKind(label);
    const copy: Record<string, Record<string, string>> = {
      tr: {
        coffee: 'Kahve falını hazırla',
        tarot: 'Tarot sorunu hazırla',
        katina: 'Katina sorunu hazırla',
        dream: 'Rüyanı anlat',
        other: `${label} formunu hazırla`,
      },
      en: {
        coffee: 'Prepare your coffee reading',
        tarot: 'Prepare your tarot question',
        katina: 'Prepare your Katina question',
        dream: 'Describe your dream',
        other: 'Prepare the reading form',
      },
      de: {
        coffee: 'Kaffeeorakel vorbereiten',
        tarot: 'Tarotfrage vorbereiten',
        katina: 'Katina-Frage vorbereiten',
        dream: 'Traum beschreiben',
        other: 'Formular vorbereiten',
      },
      es: {
        coffee: 'Preparar la lectura de café',
        tarot: 'Preparar la pregunta de tarot',
        katina: 'Preparar la pregunta de Katina',
        dream: 'Describe tu sueño',
        other: 'Preparar el formulario',
      },
      fr: {
        coffee: 'Préparer la voyance café',
        tarot: 'Préparer la question de tarot',
        katina: 'Préparer la question Katina',
        dream: 'Décrire votre rêve',
        other: 'Préparer le formulaire',
      },
    };
    return copy[lang]?.[kind] || copy['en'][kind];
  }
  private categoryFaqs(description: string): Array<[string, string]> {
    const blocks = String(description || '')
      .replace(/\\r\\n|\\n|\\r/g, '\n')
      .replace(/\r\n?/g, '\n')
      .split(/\n{2,}/)
      .map((value) => value.trim())
      .filter(Boolean);
    const faqs: Array<[string, string]> = [];
    for (let i = 0; i < blocks.length - 1; i++) {
      if (!/^###\s+/.test(blocks[i])) continue;
      const question = blocks[i].replace(/^###\s+/, '').trim();
      const answer = blocks[i + 1]
        .replace(/^[-*#]+\s*/, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .trim();
      if (question && answer && !/^#{2,3}\s+/.test(blocks[i + 1])) faqs.push([question, answer]);
    }
    return faqs.slice(0, 12);
  }
  protected categoryImage(value: string) {
    return String(value || '').replace(/\[size\]|\{size\}|%5Bsize%5D/gi, 'b');
  }
  protected categoryDescriptionParagraphs(): string[] {
    const raw = String(this.category()?.description || '')
      .replace(/<br\s*\/?\s*>/gi, '\n')
      .replace(/<\/p\s*>/gi, '\n\n')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/gi, ' ');
    return raw
      .split(/\n+/)
      .map((value) => value.replace(/\s+/g, ' ').trim())
      .filter(Boolean);
  }
  protected categoryLink(item: FortuneSaleCategory) {
    return item.isFree
      ? this.i18n.routePath('freeFortune')
      : this.i18n.routePath('category', { slug: this.categorySlug(item) });
  }
  protected categorySlug(item: FortuneSaleCategory) {
    const persisted = String(item.slug || '').trim();
    if (persisted) return persisted;
    return (
      String(item.title || '')
        .toLocaleLowerCase(this.i18n.locale())
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ı/g, 'i')
        .replace(/ğ/g, 'g')
        .replace(/ü/g, 'u')
        .replace(/ş/g, 's')
        .replace(/ö/g, 'o')
        .replace(/ç/g, 'c')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || String(item.categoryID)
    );
  }
  protected ratingPercent(rating: number) {
    return Math.round(Math.max(0, Math.min(5, Number(rating) || 0)) * 20);
  }
  protected offerPresenceKind(item: FortuneSaleOffer): 'online' | 'duty' | 'reading' | 'offline' {
    const value = String(item.presenceStatus || '')
      .toLowerCase()
      .trim();
    return value === 'on_duty' || value === 'nobetci' || value === 'nöbetçi'
      ? 'duty'
      : value === 'reading' ||
          value === 'fortune' ||
          value === 'falda' ||
          value === 'busy' ||
          value === 'meşgul'
        ? 'reading'
        : [
              'online',
              'intraday',
              'today',
              'available',
              'active',
              'live',
              'çevrimiçi',
              'cevrimici',
              'müsait',
              'musait',
            ].includes(value)
          ? 'online'
          : 'offline';
  }
  protected offerPresenceLabel(item: FortuneSaleOffer) {
    const kind = this.offerPresenceKind(item);
    return kind === 'duty'
      ? this.copy().duty
      : kind === 'reading'
        ? this.copy().reading
        : kind === 'online'
          ? this.copy().online
          : this.copy().offline;
  }
}
